import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import DeleteIcon from '@atlaskit/icon/core/delete';
import BlockSyncedIcon from '@atlaskit/icon-lab/core/block-synced';
import SkipUnskipIcon from '@atlaskit/icon-lab/core/skip-unskip';

import { useAnalytics } from 'src/components/settings/analytics';

import { useBulkTestCaseActions } from '../hooks/use-bulk-test-case-actions';
import { messages } from '../messages';
import { RemoveBulkTestCasesModal } from '../modals/RemoveBulkTestCasesModal';
import { ExecutionStrategy, TestCaseAttribute, type TestCase } from '../types';

type Props = {
  selectedTests: TestCase[];
  onActionComplete: () => void;
};

export const BulkTestCaseActions = ({
  selectedTests,
  onActionComplete,
}: Props) => {
  const { formatMessage } = useIntl();
  const { publishButtonClickEvent } = useAnalytics({
    source: 'bulkTestCaseActions',
  });

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const {
    bulkUpdateAttributes,
    bulkUpdateExecutionStrategy,
    bulkDeleteTestCases,
    isPending,
  } = useBulkTestCaseActions({
    onSuccess: () => {
      setIsRemoveModalOpen(false);
      onActionComplete();
    },
  });

  const testUuids = useMemo(
    () => selectedTests.map(test => test.uuid),
    [selectedTests]
  );

  const allFlaky = useMemo(
    () =>
      selectedTests.length > 0 &&
      selectedTests.every(test =>
        test.attributes.includes(TestCaseAttribute.FLAKY)
      ),
    [selectedTests]
  );

  const allQuarantined = useMemo(
    () =>
      selectedTests.length > 0 &&
      selectedTests.every(
        test => test.executionStrategy === ExecutionStrategy.QUARANTINED
      ),
    [selectedTests]
  );

  const onToggleFlaky = useCallback(() => {
    publishButtonClickEvent('bulkToggleFlaky', {
      action: allFlaky ? 'unmarkedFlaky' : 'markedFlaky',
      count: testUuids.length,
    });
    bulkUpdateAttributes({
      testUuids,
      attributes: allFlaky ? [] : [TestCaseAttribute.FLAKY],
    });
  }, [allFlaky, bulkUpdateAttributes, publishButtonClickEvent, testUuids]);

  const onToggleQuarantine = useCallback(() => {
    publishButtonClickEvent('bulkToggleQuarantined', {
      action: allQuarantined ? 'unmarkedQuarantined' : 'markedQuarantined',
      count: testUuids.length,
    });
    bulkUpdateExecutionStrategy({
      testUuids,
      executionStrategy: allQuarantined
        ? ExecutionStrategy.DEFAULT
        : ExecutionStrategy.QUARANTINED,
    });
  }, [
    allQuarantined,
    bulkUpdateExecutionStrategy,
    publishButtonClickEvent,
    testUuids,
  ]);

  const onRemoveClick = useCallback(() => {
    publishButtonClickEvent('bulkRemoveTestCases', { count: testUuids.length });
    setIsRemoveModalOpen(true);
  }, [publishButtonClickEvent, testUuids.length]);

  const onRemoveModalClose = useCallback(() => setIsRemoveModalOpen(false), []);

  const onRemoveConfirm = useCallback(
    (uuids: string[]) => bulkDeleteTestCases({ testUuids: uuids }),
    [bulkDeleteTestCases]
  );

  const isDisabled = isPending || selectedTests.length === 0;

  return (
    <>
      <Button
        appearance="subtle"
        isDisabled={isDisabled}
        iconBefore={BlockSyncedIcon}
        onClick={onToggleFlaky}
        testId="bulk-test-case-actions-toggle-flaky"
      >
        {formatMessage(allFlaky ? messages.unmarkFlaky : messages.markFlaky)}
      </Button>
      <Button
        appearance="subtle"
        isDisabled={isDisabled}
        iconBefore={SkipUnskipIcon}
        onClick={onToggleQuarantine}
        testId="bulk-test-case-actions-toggle-quarantine"
      >
        {formatMessage(
          allQuarantined
            ? messages.unmarkAsQuarantined
            : messages.markAsQuarantined
        )}
      </Button>
      <Button
        appearance="subtle"
        isDisabled={isDisabled}
        iconBefore={DeleteIcon}
        onClick={onRemoveClick}
        testId="bulk-test-case-actions-remove"
      >
        {formatMessage(messages.removeTest)}
      </Button>
      {isRemoveModalOpen && (
        <RemoveBulkTestCasesModal
          isOpen={isRemoveModalOpen}
          testUuids={testUuids}
          isPending={isPending}
          onClose={onRemoveModalClose}
          onConfirm={onRemoveConfirm}
        />
      )}
    </>
  );
};
